/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { useFeatureFlag } from '@/features/globalConfig';
import cx from 'classnames';
import { SmartLink as Link } from '../../../../utils/smartLink';
import { Text, View } from '@az/starc-ui';
import { ShareLinkButton } from '@/features/sharableLink';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { RewardsBadge } from '@/features/rewards/components/RewardsBadge';
import { isBonusRewardsBadgeShown } from '@/features/shelf/components/PageTemplates/Shelf/ResultsList/utils';
import { type ProductModel } from '@/api/types/products-types';

const labelMap = {
  lbl_ShopAll: 'label_Homepage_content_ShopAll',
};

type TitleNotesV2Props = {
  productTitle: string;
  productBrand: string;
  brandURL: string;
  shouldShowSkeleton?: boolean;
  productDetails?: ProductModel;
  purchaseDatePaddingEnabled?: boolean;
};

const PdpTitleNotesV2 = (props: TitleNotesV2Props) => {
  const brandProfileEnabled: boolean = useFeatureFlag('BRAND_PROFILE_FEATURE_ENABLED') === 'true';
  const labels = useLabels(labelMap);
  const { productTitle, productBrand, brandURL, purchaseDatePaddingEnabled } = props;
  const showShopAllLink = brandProfileEnabled && !!brandURL && !!productBrand;
  const ariaLabel = `${labels.lbl_ShopAll} ${productBrand}`;
  const isSharableLinksEnabled = useFeatureFlag('SHARABLE_LINKS_ENABLED') === 'true';
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';
  const isBonusRewardsBadgeEnabled = useFeatureFlag('BONUS_REWARDS_BADGE_ENABLE') === 'true';
  const locale = useLocale();
  const isUS = locale === countryCodes.us;
  const showRewardsBadge = isBonusRewardsBadgeShown(
    props.productDetails?.bonusRewardEligible,
    props.productDetails?.bonusRewardAmount,
    props.productDetails?.bonusRewardStartDate,
    props.productDetails?.bonusRewardEndDate,
    isBonusRewardsBadgeEnabled,
    isUS,
  );

  return (
    <View
      direction="row"
      width="100%"
      padding={{ l: purchaseDatePaddingEnabled ? [2, 0, 0, 0] : [3, 0, 0, 0], m: 0, s: 0 }}
    >
      <View.Item
        columns={isSharableLinksEnabled ? 11 : 12}
        className={cx({ [styles.titleContainer]: !isCustomReviewsEnabled })}
      >
        {/* Rewards badge */}
        {showRewardsBadge && (
          <View padding={[0, 0, 2, 0]}>
            <RewardsBadge
              amount={props?.productDetails?.bonusRewardAmount}
              rewardsHeader={props?.productDetails?.bonusRewardCopy}
              rewardsBody={props?.productDetails?.bonusRewardLegal}
            />
          </View>
        )}
        <Text
          variant="large-body"
          weight="bold"
          attributes={{ 'data-testid': 'product-title' }}
          className={cx(props.shouldShowSkeleton && styles.loadingInformation)}
          as="h1"
        >{`${productTitle}`}</Text>
        {showShopAllLink && (
          <View padding={[2, 0, 0, 0]} className={styles.shopAllBrandLink}>
            <Link to={brandURL} ariaLabel={ariaLabel}>
              <Text
                variant="text-link"
                decoration="underline"
                className={styles.textLinkOverride}
              >{`${labels.lbl_ShopAll} ${productBrand}`}</Text>
            </Link>
          </View>
        )}
      </View.Item>
      {isSharableLinksEnabled ? (
        <View.Item grow className={styles.verticalOffset}>
          <ShareLinkButton pageName="pdp" justify="end" />
        </View.Item>
      ) : null}
    </View>
  );
};

export default PdpTitleNotesV2;
